import React, { useState, useEffect } from "react";
import { Box, Typography, InputBase, IconButton, useTheme } from "@mui/material";
import { Send, Close } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import UserImage from "../../components/UserImage";
import FlexBetween from "../../components/FlexBetween";

const ChatBox = ({ onClose }) => {
  const [friends, setFriends] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [messages, setMessages] = useState({});
  const [currentMessage, setCurrentMessage] = useState("");
  const [socket, setSocket] = useState(null);

  const { palette } = useTheme();
  const user = useSelector((state) => state.user);
  const token = useSelector((state) => state.token);

  useEffect(() => {
    // Connect to local backend for socket since the render one doesn't have socket code yet
    const newSocket = io("https://dropie-service.onrender.com"); 
    setSocket(newSocket);

    newSocket.emit("join", user._id);

    newSocket.on("receiveMessage", (data) => {
      setMessages((prev) => {
        const friendId = data.senderId === user._id ? data.receiverId : data.senderId;
        const currentChat = prev[friendId] || [];
        return {
          ...prev,
          [friendId]: [...currentChat, data],
        };
      });
    });

    return () => newSocket.disconnect();
  }, [user._id]);

  useEffect(() => {
    const getFriends = async () => {
      const response = await fetch(`https://dropie-service.onrender.com/users/${user._id}/friends`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setFriends(data);
    };
    getFriends();
  }, [user._id, token]);

  const sendMessage = () => {
    if (currentMessage.trim() && socket && selectedFriend) {
      const msgData = {
        senderId: user._id,
        receiverId: selectedFriend._id,
        text: currentMessage,
      };
      socket.emit("sendMessage", msgData);
      
      // Update local UI for sender
      setMessages((prev) => {
        const currentChat = prev[selectedFriend._id] || [];
        return {
          ...prev,
          [selectedFriend._id]: [...currentChat, msgData],
        };
      });
      
      setCurrentMessage("");
    }
  };

  return (
    <Box
      position="fixed"
      bottom="20px"
      right="20px"
      width="400px"
      height="450px"
      backgroundColor={palette.background.alt}
      borderRadius="0.5rem"
      boxShadow="0 4px 15px rgba(0,0,0,0.3)"
      zIndex="1000"
      display="flex"
    >
      {/* Sidebar: Friends List */}
      <Box width="35%" borderRight={`1px solid ${palette.neutral.light}`} overflow="auto">
        <FlexBetween p="1rem" borderBottom={`1px solid ${palette.neutral.light}`}>
          <Typography fontWeight="bold">Chats</Typography>
          <IconButton onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </FlexBetween>
        <Box p="0.5rem">
          {friends.map((friend) => (
            <Box
              key={friend._id}
              display="flex"
              alignItems="center"
              gap="0.5rem"
              p="0.5rem"
              sx={{
                cursor: "pointer",
                backgroundColor: selectedFriend?._id === friend._id ? palette.neutral.light : "transparent",
                borderRadius: "0.25rem",
                "&:hover": { backgroundColor: palette.neutral.light }
              }}
              onClick={() => setSelectedFriend(friend)}
            >
              <UserImage image={friend.picturePath} size="30px" />
              <Typography fontSize="0.8rem">{friend.firstName}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Main Chat Area */}
      <Box width="65%" display="flex" flexDirection="column">
        {selectedFriend ? (
          <>
            <Box p="1rem" borderBottom={`1px solid ${palette.neutral.light}`}>
              <Typography fontWeight="bold">{selectedFriend.firstName} {selectedFriend.lastName}</Typography>
            </Box>
            <Box flex="1" overflow="auto" p="1rem" display="flex" flexDirection="column" gap="0.5rem">
              {(messages[selectedFriend._id] || []).map((msg, i) => (
                <Box
                  key={i}
                  alignSelf={msg.senderId === user._id ? "flex-end" : "flex-start"}
                  backgroundColor={msg.senderId === user._id ? palette.primary.main : palette.neutral.light}
                  color={msg.senderId === user._id ? palette.background.alt : palette.neutral.main}
                  p="0.5rem"
                  borderRadius="0.5rem"
                  maxWidth="85%"
                >
                  <Typography fontSize="0.85rem">{msg.text}</Typography>
                </Box>
              ))}
            </Box>
            <Box p="0.5rem" borderTop={`1px solid ${palette.neutral.light}`} display="flex" alignItems="center">
              <InputBase
                placeholder="Type..."
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                sx={{ flex: 1, backgroundColor: palette.neutral.light, borderRadius: "1rem", px: "1rem", py: "0.2rem" }}
              />
              <IconButton onClick={sendMessage} color="primary" sx={{ ml: "0.2rem" }}>
                <Send fontSize="small" />
              </IconButton>
            </Box>
          </>
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography color={palette.neutral.medium} fontSize="0.9rem">Select a friend</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatBox;
